import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Calendar, Award, Users, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.getServices();
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const serviceCategories = [
    {
      icon: Sparkles,
      category: 'consultancy',
      title: 'Event Consultancy',
      description: 'Strategic event planning and advisory services',
      features: [
        'Concept Development & Ideation',
        'Event Feasibility Studies',
        'Program Design & Flow Architecture',
        'Vendor Advisories',
        'Budget Development & Optimization',
        'Strategic Event Recommendations',
      ],
    },
    {
      icon: Calendar,
      category: 'planning',
      title: 'Event Planning & Management',
      description: 'End-to-end event coordination and execution',
      features: [
        'Complete Event Planning',
        'On-site Coordination & Stage Management',
        'Venue Sourcing & Logistics',
        'Guest Management & Protocol',
        'Technical Production Oversight',
        'Timeline & Operations Management',
      ],
    },
    {
      icon: Award,
      category: 'branding',
      title: 'Branding, Graphics & Design',
      description: 'Visual identity and creative design solutions',
      features: [
        'Visual Identity Development',
        'Event Branding Collateral',
        'Stage/Set Design Concepts',
        'Digital Graphics & Promotional Materials',
        'Banners, Backdrops & Signage',
        'Social Media Creative Suites',
      ],
    },
    {
      icon: Users,
      category: 'production',
      title: 'Creative Production Support',
      description: 'Innovative production and experiential design',
      features: [
        'Storyboarding & Creative Direction',
        'Event Photography & Videography',
        'Scriptwriting & Ceremony Content',
        'Creative Consulting for Experiential Events',
        'Stage, Ambience & Décor Concepts',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="services-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4 block">
              Our Expertise
            </span>
            <h1 className="text-5xl md:text-7xl font-medium leading-[0.9] mb-6 tracking-tight">
              Services That
              <br />
              <span className="text-gradient">Elevate</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-white/70 max-w-3xl">
              Comprehensive event solutions designed to transform your vision into reality.
              From concept to execution, we handle every detail with precision and creativity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {serviceCategories.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start"
                data-testid={`service-category-${service.category}`}
              >
                <div className="space-y-6">
                  <service.icon className="text-[#D4AF37]" size={48} />
                  <h2 className="text-4xl md:text-5xl font-normal leading-tight">{service.title}</h2>
                  <p className="text-lg font-light leading-relaxed text-white/70">
                    {service.description}
                  </p>
                  <Link to="/contact">
                    <Button className="bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-8 py-4 mt-4">
                      Request Quote
                    </Button>
                  </Link>
                </div>

                <div className="bg-[#0A0A0F] border border-white/5 p-8 space-y-4">
                  <h3 className="text-xl font-semibold text-[#D4AF37] mb-6 uppercase tracking-wider">
                    What's Included
                  </h3>
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start space-x-3">
                      <Check className="text-[#D4AF37] mt-1 flex-shrink-0" size={20} />
                      <span className="text-white/80">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-medium leading-tight mb-6">
              Let's Create Your
              <br />
              <span className="text-gradient">Perfect Event</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-white/70 mb-8">
              Contact us today to discuss your event needs and get a customized proposal
            </p>
            <Link to="/contact">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-10 py-6">
                Get In Touch
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;